/**
 * Track 4: page side of Web Push — permission, subscription and the server hand-over.
 *
 * Uses the page's existing service worker registration if there is one (the PWA shell's), otherwise registers
 * the standalone `/push-sw.js`. A push subscription belongs to the registration's scope, so when the PWA
 * worker later takes over the same scope the subscription stays valid.
 */
import { subscribePush, unsubscribePush } from '../api/social.ts';

export type PushSupport = 'unsupported' | 'denied' | 'default' | 'granted';

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return Notification.permission === 'denied' ? 'denied' : (Notification.permission as 'default' | 'granted');
}

/** VAPID public keys are base64url; PushManager wants bytes. */
export function urlBase64ToBytes(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration()) ?? navigator.serviceWorker.register('/push-sw.js', { scope: '/' });
}

const b64url = (buf: ArrayBuffer | null): string => {
  if (!buf) return '';
  let s = '';
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** Asks for permission (a click handler must call this), subscribes and registers the subscription on the server. */
export async function enablePush(vapidPublicKey: string): Promise<'ok' | 'denied' | 'unsupported'> {
  if (pushSupport() === 'unsupported') return 'unsupported';
  if ((await Notification.requestPermission()) !== 'granted') return 'denied';
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription())
    ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(vapidPublicKey) as BufferSource });
  await subscribePush({ endpoint: sub.endpoint, keys: { p256dh: b64url(sub.getKey('p256dh')), auth: b64url(sub.getKey('auth')) } });
  return 'ok';
}

export async function disablePush(): Promise<void> {
  if (pushSupport() === 'unsupported') return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await unsubscribePush(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe();
  }
}
